<template>
    <AmModal
        :open="open"
        size="large"
        eyebrow="ТАРИФЫ"
        accent-eyebrow
        title="Выберите подходящий план"
        subtitle="Расширьте лимиты запросов и подключите дополнительные источники. Можно отменить в любой момент."
        @close="$emit('close')"
    >
            <div v-if="loading" class="state-block">Загружаем актуальные тарифы…</div>
            <div v-else-if="error" class="state-block state-block--error">
                Не удалось загрузить тарифы. Попробуйте обновить страницу.
            </div>
            <div v-else-if="visiblePlans.length === 0" class="state-block">
                Сейчас тарифы недоступны — обратитесь к менеджеру.
            </div>

            <template v-else>
                <div v-if="hasAnnual" class="billing-switch" role="tablist" aria-label="Период оплаты">
                    <button
                        type="button"
                        class="billing-option"
                        :class="{ 'billing-option--active': billingPeriod === 'month' }"
                        role="tab"
                        :aria-selected="billingPeriod === 'month'"
                        @click="billingPeriod = 'month'"
                    >
                        Помесячно
                    </button>
                    <button
                        type="button"
                        class="billing-option"
                        :class="{ 'billing-option--active': billingPeriod === 'year' }"
                        role="tab"
                        :aria-selected="billingPeriod === 'year'"
                        @click="billingPeriod = 'year'"
                    >
                        На год
                        <span class="billing-badge">−50%</span>
                    </button>
                </div>

                <div class="plans" :class="plansColsClass">
                <article
                    v-for="planItem in visiblePlans"
                    :key="planItem.code"
                    class="plan-card"
                    :class="{
                        'plan-card--current': planItem.code === currentPlan,
                        'plan-card--accent': planItem.code === accentCode,
                        'plan-card--coming-soon': planItem.is_coming_soon,
                    }"
                >
                    <div v-if="planItem.is_coming_soon" class="plan-tag plan-tag--soon">Скоро</div>
                    <div v-else-if="planItem.code === accentCode" class="plan-tag">Популярный</div>

                    <div class="plan-name">{{ planItem.name }}</div>
                    <div class="plan-price">
                        <span class="price-value">{{ formatPrice(planItem) }}</span>
                        <span v-if="formatPriceSuffix(planItem)" class="price-suffix">
                            {{ formatPriceSuffix(planItem) }}
                        </span>
                    </div>
                    <div class="plan-note">{{ formatPlanNote(planItem) }}</div>

                    <ul class="plan-features">
                        <li
                            v-for="(feature, idx) in buildFeatures(planItem)"
                            :key="idx"
                            class="plan-feature"
                            :class="{ 'plan-feature--off': feature.off }"
                        >
                            <span class="feature-mark" aria-hidden="true">{{ feature.off ? '—' : '✓' }}</span>
                            <span>{{ feature.text }}</span>
                        </li>
                    </ul>

                    <button
                        v-if="planItem.code === currentPlan"
                        type="button"
                        class="plan-cta plan-cta--current"
                        disabled
                    >
                        Текущий план
                    </button>
                    <button
                        v-else-if="planItem.is_coming_soon"
                        type="button"
                        class="plan-cta plan-cta--soon"
                        disabled
                    >
                        Скоро
                    </button>
                    <button
                        v-else
                        type="button"
                        class="plan-cta"
                        :class="{ 'plan-cta--primary': planItem.code === accentCode }"
                        @click="selectPlan(planItem)"
                    >
                        {{ ctaLabel(planItem) }}
                    </button>
                </article>
                </div>
            </template>

        <template #footer>
            <div class="modal-foot">
                <span>Безопасная оплата — Visa / MasterCard / МИР / СБП</span>
                <span>Чек и реквизиты — на email</span>
            </div>
        </template>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import { getChatBillingPlans } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    currentPlan: { type: String, default: 'free' },
})

const emit = defineEmits(['close', 'select'])

const plans = ref([])
const loading = ref(false)
const error = ref(false)
const billingPeriod = ref('month')
let loaded = false

async function loadPlans() {
    loading.value = true
    error.value = false
    try {
        plans.value = await getChatBillingPlans()
        loaded = true
    } catch (e) {
        error.value = true
        plans.value = []
    } finally {
        loading.value = false
    }
}

watch(
    () => props.open,
    (next) => {
        if (next && !loaded) {
            loadPlans()
        }
    },
    { immediate: true },
)

const visiblePlans = computed(() => plans.value)

// Сетка карточек: 4 и более тарифов — по 2 в строке (иначе тесно и некрасиво),
// меньше — по 3 в строке. На мобильном сводится к 1 колонке (см. media query).
const plansColsClass = computed(() =>
    visiblePlans.value.length >= 4 ? 'plans--two' : 'plans--three',
)

// Переключатель «Год» показываем только если хотя бы у одного тарифа есть годовая цена.
const hasAnnual = computed(() =>
    visiblePlans.value.some((p) => Number(p.annual_price_cents) > 0),
)

// Эффективный период для конкретного тарифа: год — только если у него есть годовая цена.
function planBillingPeriod(plan) {
    return billingPeriod.value === 'year' && Number(plan.annual_price_cents) > 0
        ? 'year'
        : 'month'
}

function selectPlan(plan) {
    emit('select', { code: plan.code, billingPeriod: planBillingPeriod(plan) })
}

// «Популярный» — определяем как «не текущий, не coming_soon, с минимальной положительной ценой».
// Так бейдж сам прицепится к Pro, без хардкода кода тарифа в шаблоне.
const accentCode = computed(() => {
    const candidates = visiblePlans.value.filter(
        (p) => !p.is_coming_soon && p.price_cents > 0 && p.code !== props.currentPlan,
    )
    if (candidates.length === 0) return null
    const cheapestPaid = candidates.reduce(
        (acc, p) => (acc === null || p.price_cents < acc.price_cents ? p : acc),
        null,
    )
    return cheapestPaid?.code || null
})

function formatPrice(plan) {
    if (plan.is_coming_soon) return 'Скоро'
    if (!plan.price_cents) return '0 ₽'
    const cents = planBillingPeriod(plan) === 'year' ? plan.annual_price_cents : plan.price_cents
    const rub = Math.round(cents / 100)
    return `${rub.toLocaleString('ru-RU')} ₽`
}

function formatPriceSuffix(plan) {
    if (plan.is_coming_soon) return ''
    if (!plan.price_cents) {
        if (plan.is_trial && plan.period_days) return `на ${formatPeriodLabel(plan.period_days)}`
        return '/мес'
    }
    if (planBillingPeriod(plan) === 'year') return '/год'
    return '/мес'
}

function formatPeriodLabel(days) {
    if (days % 7 === 0 && days < 30) {
        const weeks = days / 7
        return `${weeks} нед.`
    }
    if (days >= 30) return `${Math.round(days / 30)} мес.`
    return `${days} дн.`
}

function formatPlanNote(plan) {
    if (plan.description) return plan.description
    if (!plan.price_cents) return 'Бесплатно'
    return ''
}

function ctaLabel(plan) {
    if (!plan.price_cents) return 'Перейти на ' + plan.name
    return 'Оформить ' + plan.name
}

function formatUsers(plan) {
    const users = Number(plan.max_users || 0)
    if (users <= 0) return 'Неограниченное число участников'
    if (users === 1) return '1 пользователь'
    return `До ${users} пользователей`
}

function formatHistory(plan) {
    if (!plan.max_thread_age_days) return null
    const days = plan.max_thread_age_days
    if (days >= 365) return `История ${Math.round(days / 365)} год${days >= 365 * 2 ? 'а' : ''}`
    if (days >= 30) return `История ${Math.round(days / 30)} мес`
    return `История ${days} дн`
}

function formatSources(plan) {
    const sources = plan.sources || []
    if (sources.length === 0) return null
    const names = sources.map((s) => s.name).filter(Boolean)
    if (names.length === 0) return null
    return `Источники: ${names.join(', ')}`
}

function buildFeatures(plan) {
    const features = []
    if (plan.is_trial) {
        features.push({ text: 'Полный функционал платформы' })
    }
    features.push({ text: formatUsers(plan) })
    const history = formatHistory(plan)
    if (history) features.push({ text: history })
    const sources = formatSources(plan)
    if (sources) features.push({ text: sources })
    features.push({
        text: 'Экспорт PDF / Excel / Share-link',
        off: !plan.allow_share,
    })
    features.push({
        text: 'Сохранённые отчёты «Мои отчёты»',
        off: !plan.allow_my_reports,
    })
    features.push({ text: 'Поиск по VIN', off: !plan.allow_vin })
    features.push({ text: 'ИНН поиск', off: !plan.allow_inn })
    if (plan.personal_manager) {
        features.push({ text: 'Личный менеджер' })
    }
    return features
}
</script>

<style scoped>
.state-block {
    padding: 36px 20px;
    text-align: center;
    color: #5f5f5f;
    font-size: 13px;
    background: #f7f7f7;
    border-radius: 10px;
}

.state-block--error {
    color: #C0050F;
    background: #FFEAE7;
}

.billing-switch {
    display: flex;
    align-items: stretch;
    gap: 4px;
    padding: 4px;
    margin: 0 auto 18px;
    background: #f0f0f0;
    border-radius: 10px;
    width: fit-content;
}

.billing-option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: #5f5f5f;
    background: transparent;
    border: 0;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.billing-option--active {
    background: white;
    color: #23282C;
    box-shadow: 0 1px 3px rgba(21, 26, 34, 0.12);
}

.billing-badge {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    color: white;
    background: #E30613;
    padding: 2px 6px;
    border-radius: 999px;
}

.plans {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

.plans--two {
    grid-template-columns: repeat(2, 1fr);
}

.plans--three {
    grid-template-columns: repeat(3, 1fr);
}

.plan-card {
    position: relative;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 18px 18px 16px;
    background: #f7f7f7;
    display: flex;
    flex-direction: column;
}

.plan-card--accent {
    border-color: #23282C;
    background: white;
    box-shadow: 0 6px 18px rgba(21, 26, 34, 0.08);
}

.plan-card--current {
    border-color: #198FA0;
    background: rgba(25, 143, 160, 0.05);
}

.plan-card--coming-soon {
    background: repeating-linear-gradient(
        135deg,
        #f7f7f7,
        #f7f7f7 12px,
        #e7e7e7 12px,
        #e7e7e7 24px
    );
    border-color: #e0e0e0;
}

.plan-tag {
    position: absolute;
    top: -10px;
    left: 18px;
    background: #23282C;
    color: white;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 4px;
}

.plan-tag--soon {
    background: #B07E18;
}

.plan-name {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #23282C;
    margin-bottom: 6px;
}

.plan-price {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
}

.price-value {
    font-size: 26px;
    font-weight: 700;
    color: #23282C;
    letter-spacing: -0.3px;
}

.price-suffix {
    font-size: 13px;
    color: #5f5f5f;
}

.plan-note {
    font-size: 11.5px;
    color: #888888;
    margin-bottom: 14px;
    min-height: 16px;
    line-height: 1.4;
}

.plan-features {
    list-style: none;
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

.plan-feature {
    display: flex;
    gap: 8px;
    font-size: 12.5px;
    color: #23282C;
    line-height: 1.45;
}

.plan-feature--off {
    color: #B4B2A9;
    text-decoration: line-through;
}

.feature-mark {
    flex-shrink: 0;
    width: 16px;
    text-align: center;
    color: #198FA0;
    font-weight: 600;
}

.plan-feature--off .feature-mark {
    color: #c0c0c0;
}

.plan-cta {
    width: 100%;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid #23282C;
    background: white;
    color: #23282C;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.plan-cta:hover {
    background: #23282C;
    color: white;
}

.plan-cta--primary {
    background: #E30613;
    border-color: #E30613;
    color: white;
}

.plan-cta--primary:hover {
    background: #c30410;
    border-color: #c30410;
    color: white;
}

.plan-cta--current {
    background: rgba(25, 143, 160, 0.12);
    border-color: rgba(25, 143, 160, 0.4);
    color: #198FA0;
    cursor: default;
}

.plan-cta--current:hover {
    background: rgba(25, 143, 160, 0.12);
    color: #198FA0;
}

.plan-cta--soon {
    background: rgba(176, 126, 24, 0.12);
    border-color: rgba(176, 126, 24, 0.4);
    color: #8A6810;
    cursor: not-allowed;
}

.plan-cta--soon:hover {
    background: rgba(176, 126, 24, 0.12);
    color: #8A6810;
}

.modal-foot {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 8px;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #e7e7e7;
    font-size: 11.5px;
    color: #888888;
    letter-spacing: 0.4px;
}

@media (max-width: 820px) {
    .plans {
        grid-template-columns: 1fr;
    }

    .modal {
        padding: 22px;
    }

    .modal-title {
        font-size: 19px;
    }
}
</style>
